import { type User } from "oidc-client-ts";

import { localStorage } from "~/utilities/storage";

const ACTIVE_CLIENT_ID_STORAGE_KEY = "active_client_id";

export function getActiveClientIdStorageKey(user: User): string {
  return `${ACTIVE_CLIENT_ID_STORAGE_KEY}:${user.profile.sub}`;
}

interface KeycloakResourceAccessEntry {
  roles?: string[];
}

interface KeycloakTokenPayload {
  realm_access?: {
    roles?: string[];
  };
  resource_access?: Record<string, KeycloakResourceAccessEntry>;
}

function setIntersection<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  if (typeof setA.intersection === "function") {
    return setA.intersection(setB);
  }
  const _intersection = new Set<T>();
  for (const elem of setB) {
    if (setA.has(elem)) {
      _intersection.add(elem);
    }
  }
  return _intersection;
}

export function decodeAccessTokenPayload(
  user: User | null | undefined,
): KeycloakTokenPayload | null {
  if (!user || typeof user.access_token !== "string") {
    return null;
  }

  try {
    const payload = user.access_token.split(".")[1];
    if (!payload) {
      console.error("Invalid token format: missing payload");
      return null;
    }
    const binary = atob(payload);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    console.error("Failed to decode access token payload:", error);
    return null;
  }
}

export function getUserRoles(user: User | null | undefined): string[] | null {
  const decoded = decodeAccessTokenPayload(user);
  if (!decoded) {
    return null;
  }

  return decoded.realm_access?.roles ?? [];
}

export function userHasRealmAccessRole(
  user: User | null | undefined,
  requiredRoles: Set<string>,
): boolean {
  const userRoles = getUserRoles(user);

  if (userRoles === null) {
    return false;
  }

  const userRolesSet = new Set(userRoles);

  const intersection = setIntersection(userRolesSet, requiredRoles);
  return intersection.size > 0;
}

export function getClientRolesByClientId(
  user: User | null | undefined,
  ignoredClientIds: string[] = [],
): Map<string, Set<string>> {
  const clientRoles = new Map<string, Set<string>>();
  const decoded = decodeAccessTokenPayload(user);
  if (!decoded?.resource_access) {
    return clientRoles;
  }

  const ignoredClientIdsSet = new Set(ignoredClientIds);
  const sortedClientIds = Object.keys(decoded.resource_access).sort();

  for (const clientId of sortedClientIds) {
    if (ignoredClientIdsSet.has(clientId)) {
      continue;
    }
    const resource = decoded.resource_access[clientId];
    const roles = resource?.roles ?? [];
    if (roles.length === 0) {
      continue;
    }
    clientRoles.set(clientId, new Set(roles));
  }

  return clientRoles;
}

export function userHasClientAccessRole(
  user: User | null | undefined,
  clientId: string | null | undefined,
  requiredRoles: Set<string>,
  ignoredClientIds: string[],
  clientRolesByClientId?: Map<string, Set<string>>,
): boolean {
  if (!clientId) {
    return false;
  }

  const rolesByClient =
    clientRolesByClientId ?? getClientRolesByClientId(user, ignoredClientIds);
  const roles = rolesByClient.get(clientId);
  if (!roles) {
    return false;
  }
  if (requiredRoles.size === 0) {
    return roles.size > 0;
  }
  const intersection = setIntersection(roles, requiredRoles);
  return intersection.size > 0;
}

export function getFirstClientIdFromClientRoles(
  clientRolesByClientId: Map<string, Set<string>>,
  requiredClientRoles: Set<string>,
): string | null {
  for (const [clientId, roles] of clientRolesByClientId.entries()) {
    if (requiredClientRoles.size === 0) {
      return clientId;
    }

    const intersection = setIntersection(roles, requiredClientRoles);
    if (intersection.size > 0) {
      return clientId;
    }
  }

  return null;
}

export function getFirstClientId(
  user: User | null | undefined,
  requiredClientRoles: Set<string>,
  ignoredClientIds: string[],
): string | null {
  const clientRolesByClientId = getClientRolesByClientId(
    user,
    ignoredClientIds,
  );
  return getFirstClientIdFromClientRoles(
    clientRolesByClientId,
    requiredClientRoles,
  );
}

export function hasAnyRole(
  user: User | null | undefined,
  realmRequiredRoles: Set<string>,
  clientRequiredRoles: Set<string>,
  ignoredClientIds: string[],
  clientId?: string | null,
): boolean {
  if (userHasRealmAccessRole(user, realmRequiredRoles)) {
    return true;
  }

  if (!clientId) {
    return false;
  }

  const clientRolesByClientId = getClientRolesByClientId(
    user,
    ignoredClientIds,
  );
  return userHasClientAccessRole(
    user,
    clientId,
    clientRequiredRoles,
    ignoredClientIds,
    clientRolesByClientId,
  );
}

export function resolveActiveClientId(
  user: User,
  clientRequiredRoles: Set<string>,
  realmRequiredRoles: Set<string>,
  ignoredClientIds: string[],
  preferredClientId?: string | null,
): string | null {
  if (preferredClientId) {
    if (
      hasAnyRole(
        user,
        realmRequiredRoles,
        clientRequiredRoles,
        ignoredClientIds,
        preferredClientId,
      )
    ) {
      return preferredClientId;
    }

    return null;
  }

  if (userHasRealmAccessRole(user, realmRequiredRoles)) {
    return null;
  }

  const storedClientId = localStorage.getItem<string>(
    getActiveClientIdStorageKey(user),
  );

  if (
    storedClientId &&
    hasAnyRole(
      user,
      realmRequiredRoles,
      clientRequiredRoles,
      ignoredClientIds,
      storedClientId,
    )
  ) {
    return storedClientId;
  }

  return getFirstClientId(user, clientRequiredRoles, ignoredClientIds);
}
